import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "../../App"; 
import DashboardHeader from "../../components/app_components/DashboardHeader";
import DashboardSidebar from "../../components/app_components/DashboardSidebar";
import { 
  Home, Bell, BellDot, ShoppingBag, Settings, 
  Users, ShoppingCart, Loader2, User, Mail, 
  Globe, Shield, ChevronRight, Share2, CheckCircle, 
  XCircle, Trash2, AlertTriangle, Crown, Sparkles
} from 'lucide-react';
import { useTranslation } from '../../contexts/LanguageContext';
import FlagLanguageSelector from '../../components/LanguageSelector/FlagLanguageSelector';

const AccountSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetStatus, setResetStatus] = useState({
    loading: false,
    success: false,
    error: null
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteStatus, setDeleteStatus] = useState({
    loading: false,
    success: false,
    error: null
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { t } = useTranslation();

  // Fetch unread notifications count
  const fetchUnreadNotifications = async () => {
    try {
      if (!httpClient) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        if (!httpClient) {
          setError('Not authenticated');
          setLoading(false);
          return;
        }
        
        const response = await httpClient.request('/api/user/account');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setUserData(data);
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    } else {
      navigate("/demo");
    }
  }, [user, navigate, httpClient]);

  // Setup notification polling
  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
      const interval = setInterval(() => {
        fetchUnreadNotifications();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handlePasswordReset = async () => {
    if (!userData?.email) return;
    
    setResetStatus({ loading: true, success: false, error: null });
    
    try {
      if (!httpClient) {
        setResetStatus({ loading: false, success: false, error: 'Not authenticated' });
        return;
      }
      
      const response = await httpClient.request('/api/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email: userData.email })
      });

      if (!response.ok) {
        throw new Error(response.status === 404 
          ? 'User not found' 
          : 'Failed to send reset email');
      }

      const result = await response.json();
      if (result.message) {
        setResetStatus({ loading: false, success: true, error: null });
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setResetStatus({ loading: false, success: false, error: err.message });
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== 'goodbye easysubs') {
      setDeleteStatus({
        loading: false,
        success: false,
        error: t('accountSettings.dangerZone.confirmationError')
      });
      return;
    }

    setDeleteStatus({ loading: true, success: false, error: null });
    
    try {
      if (!httpClient) {
        setDeleteStatus({ 
          loading: false, 
          success: false, 
          error: 'Not authenticated' 
        });
        return;
      }

      const response = await httpClient.request('/api/user/account', {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete account');
      }

      // Account deleted successfully
      setDeleteStatus({ loading: false, success: true, error: null });
      
      // Clear local storage and redirect
      setTimeout(() => {
        logout();
        navigate('/demo');
      }, 2000);
    } catch (err) {
      console.error('Account deletion error:', err);
      setDeleteStatus({ 
        loading: false, 
        success: false, 
        error: err.message || 'An error occurred while deleting your account' 
      });
    }
  };

  const navItems = [
    { icon: Home, label: t('partners.nav.home'), path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('partners.nav.notifications'),
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('partners.nav.marketplace'), path: "/marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs'), path: "/share-subs" },
    { icon: ShoppingBag, label: t('partners.nav.connectBank'), path: "/connectBank" },
    { icon: Users, label: t('partners.nav.partners'), path: "/partners" },
    { icon: Settings, label: t('partners.nav.settings'), path: "/account-settings" },
  ];

  // Render security section based on reset status
  const renderSecuritySection = () => {
    if (resetStatus.success) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800">{t('accountSettings.security.emailSent')}</p>
              <p className="text-sm text-green-600 mt-1">
                {t('accountSettings.security.emailSentDescription').replace('{email}', userData?.email)}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (resetStatus.loading) {
      return (
        <button 
          disabled
          className="w-full flex justify-between items-center p-4 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed"
        >
          <div>
            <p className="font-medium text-left">{t('accountSettings.security.sendingEmail')}</p>
            <p className="text-sm text-gray-500 text-left">{t('accountSettings.security.pleaseWait')}</p>
          </div>
          <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
        </button>
      );
    }

    if (resetStatus.error) {
      return (
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Couldn't send reset email</p>
                <p className="text-sm text-red-600 mt-1">
                  {resetStatus.error || 'Please try again later.'}
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handlePasswordReset}
            className="w-full flex justify-between items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div>
              <p className="font-medium text-left">{t('accountSettings.security.tryAgain')}</p>
              <p className="text-sm text-gray-500 text-left">{t('accountSettings.security.resendEmail')}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      );
    }

    return (
      <button
        onClick={handlePasswordReset}
        className="w-full flex justify-between items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <div>
          <p className="font-medium text-left">{t('accountSettings.security.resetPassword')}</p>
          <p className="text-sm text-gray-500 text-left">{t('accountSettings.security.resetDescription')}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </button>
    );
  };

  // Render account deletion section
  const renderAccountDeletionSection = () => {
    if (deleteStatus.success) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800">{t('accountSettings.dangerZone.accountDeleted')}</p>
              <p className="text-sm text-green-600 mt-1">
                {t('accountSettings.dangerZone.deletedDescription')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">{t('accountSettings.dangerZone.title')}</p>
              <p className="text-sm text-red-600 mt-1">
                {t('accountSettings.dangerZone.warning')}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowDeleteModal(true)}
          className="w-full flex justify-center items-center gap-2 p-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Trash2 className="h-5 w-5" />
          {t('accountSettings.dangerZone.deleteAccount')}
        </button>
      </div>
    );
  };

  // Delete confirmation modal
  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-500 mr-2" />
            <h3 className="text-lg font-semibold">{t('accountSettings.dangerZone.confirmDelete')}</h3>
          </div>
          
          <p className="text-gray-600 mb-4">
            {t('accountSettings.dangerZone.confirmDescription')}
          </p>
          
          <p className="text-sm text-gray-600 mb-2">
            {t('accountSettings.dangerZone.typeToConfirm')}
          </p>
          
          <input
            type="text"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            placeholder={t('accountSettings.dangerZone.confirmationText')}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
            disabled={deleteStatus.loading}
          />
          
          {deleteStatus.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-600">{deleteStatus.error}</p>
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmation('');
                setDeleteStatus({ loading: false, success: false, error: null });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={deleteStatus.loading}
            >
              {t('accountSettings.dangerZone.cancel')}
            </button>
            
            <button
              onClick={handleDeleteAccount}
              disabled={deleteStatus.loading || deleteConfirmation.toLowerCase() !== 'goodbye easysubs'}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {deleteStatus.loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('accountSettings.dangerZone.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  {t('accountSettings.dangerZone.deleteAccount')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show loading if user is not authenticated
  if (!user) {
    return (
      <div className="flex h-screen bg-gray-50 justify-center items-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={location.pathname}
        navigate={navigate}
        user={user}
        onLogout={logout}
      />

      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />
        
        <div className="p-6 space-y-6">
          <div className={`${userData?.userPlan === 'premium' 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-6 rounded-lg justify-center text-center`}
          >
            <h1 className="text-xl font-semibold mb-2">{t('accountSettings.title')}</h1>
            <p className="text-white/90">{t('accountSettings.subtitle')}</p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#253985]" />
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-yellow-800 font-medium">Error Loading Data</h3>
                <p className="text-yellow-600 mt-1">{error}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information Section */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#253985] flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    {t('accountSettings.personalInfo.title')}
                  </h2>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">{t('accountSettings.personalInfo.firstName')}</p>
                      <p className="font-medium">
                        {user?.firstName || t('accountSettings.personalInfo.notProvided')}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">{t('accountSettings.personalInfo.email')}</p>
                      <p className="font-medium flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {user?.email || t('accountSettings.personalInfo.notProvided')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Account Security Section */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#253985] flex items-center">
                    <Shield className="h-5 w-5 mr-2" />
                    {t('accountSettings.security.title')}
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-left">{t('accountSettings.security.resetPassword')}</p>
                      <p className="text-sm text-gray-500 text-left mt-1">
                        Please fix the data loading error to access security features
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information Section */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#253985] flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    {t('accountSettings.personalInfo.title')}
                  </h2>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">{t('accountSettings.personalInfo.firstName')}</p>
                      <p className="font-medium">{userData?.firstName || userData?.companyName || t('accountSettings.personalInfo.notProvided')}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">{t('accountSettings.personalInfo.lastName')}</p>
                      <p className="font-medium">{userData?.lastName || t('accountSettings.personalInfo.notProvided')}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">{t('accountSettings.personalInfo.email')}</p>
                      <p className="font-medium flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {userData?.email}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">{t('accountSettings.personalInfo.country')}</p>
                      <p className="font-medium flex items-center">
                        <Globe className="h-4 w-4 mr-2 text-gray-400" />
                        {userData?.nation || t('accountSettings.personalInfo.notSpecified')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Account Security Section */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#253985] flex items-center">
                    <Shield className="h-5 w-5 mr-2" />
                    {t('accountSettings.security.title')}
                  </h2>
                  
                  <div className="space-y-4">
                    {renderSecuritySection()}
                  </div>
                </div>
              </div>

              {/* Language Preference Section */}
              <div className="pt-6 border-t border-gray-200">
                <FlagLanguageSelector variant="settings" />
              </div>

              {/* Subscription Plan Section */}
              <div className="pt-6 border-t border-gray-200">
                <h2 className="text-lg font-semibold text-[#253985] mb-4">{t('accountSettings.subscription.title')}</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-lg border ${userData?.userPlan === 'free' ? 'border-[#253985] bg-[#253985]/10' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold">{t('accountSettings.subscription.free.title')}</h3>
                        <p className="text-sm text-gray-500 mt-1">{t('accountSettings.subscription.free.description')}</p>
                      </div>
                      {userData?.userPlan === 'free' && (
                        <span className="bg-[#253985] text-white text-xs px-2 py-1 rounded-full">
                          {t('accountSettings.subscription.free.current')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className={`p-4 rounded-lg border ${userData?.userPlan === 'premium' ? 'border-transparent bg-gradient-to-r from-[#253985] to-amber-500 text-white' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center mb-1">
                          {userData?.userPlan === 'premium' ? (
                            <Crown className="h-5 w-5 text-amber-200 mr-2" />
                          ) : (
                            <Crown className="h-5 w-5 text-amber-500 mr-2" />
                          )}
                          <h3 className="font-semibold">{t('accountSettings.subscription.premium.title')}</h3>
                        </div>
                        <p className={`text-sm ${userData?.userPlan === 'premium' ? 'text-white/90' : 'text-gray-500'}`}>
                          {t('accountSettings.subscription.premium.description')}
                        </p>
                      </div>
                      {userData?.userPlan === 'premium' ? (
                        <span className="bg-white text-[#253985] text-xs px-2 py-1 rounded-full font-semibold">
                          {t('accountSettings.subscription.premium.current')}
                        </span>
                      ) : null}
                    </div>
                    
                    {userData?.userPlan !== 'premium' && (
                      <div className="mt-4">
                        <button 
                          onClick={() => navigate('/upgrade')}
                          className="w-full py-2.5 bg-gradient-to-r from-[#253985] to-amber-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                        >
                          {t('accountSettings.subscription.upgrade')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Deletion Section */}
              <div className="pt-6 border-t border-gray-200">
                <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center">
                  <Trash2 className="h-5 w-5 mr-2" />
                  {t('accountSettings.dangerZone.deleteAccount')}
                </h2>
                {renderAccountDeletionSection()}
              </div>
            </div>
          )}
        </div>
      </div>

      {renderDeleteModal()}
    </div>
  );
};

export default AccountSettings;